import type {
  FetchSalesPointListParams,
  SalesPoint,
} from 'Services/ServiceProviderManagement/Features/SalesPoint/types'
import type { ListResponse } from 'Services/ServiceProviderManagement/types'
import { createBaseApi } from 'Util/ApiClient'

enum TagTypes {
  SALES_POINT = 'SalesPoint',
}

export const salesPointApi = createBaseApi({
  reducerPath: 'salesPointApi',
  keepUnusedDataFor: 5,
  tagTypes: [TagTypes.SALES_POINT],
}).injectEndpoints({
  endpoints: (builder) => ({
    getSalesPoints: builder.query<
      ListResponse<SalesPoint>,
      FetchSalesPointListParams | undefined
    >({
      query: (params) => ({
        url: '/api/v1/sales-point/',
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.resources.map(
                ({ sales_point_id }) =>
                  ({ type: TagTypes.SALES_POINT, id: sales_point_id }) as const,
              ),
              { type: TagTypes.SALES_POINT, id: 'LIST' },
            ]
          : [{ type: TagTypes.SALES_POINT, id: 'LIST' }],
    }),
    getSingleSalesPoint: builder.query<SalesPoint, string>({
      query: (id) => `/api/v1/sales-point/${id}/`,
      providesTags: (_result, _error, id) => [
        { type: TagTypes.SALES_POINT, id },
      ],
    }),
    createSingleSalesPoint: builder.mutation<SalesPoint, Partial<SalesPoint>>({
      query: (data) => ({
        url: `/api/v1/sales-point/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: TagTypes.SALES_POINT, id: 'LIST' }],
    }),
    updateSingleSalesPoint: builder.mutation<
      SalesPoint,
      { id: string } & Partial<SalesPoint>
    >({
      query: (data) => ({
        url: `/api/v1/sales-point/${data.sales_point_id}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: TagTypes.SALES_POINT, id },
      ],
    }),
    deleteSingleSalesPoint: builder.mutation<Record<string, never>, string>({
      query: (id) => ({
        url: `/api/v1/sales-point/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: TagTypes.SALES_POINT, id: 'LIST' }],
    }),
  }),
})

export const {
  useGetSalesPointsQuery,
  useGetSingleSalesPointQuery,
  useCreateSingleSalesPointMutation,
  useUpdateSingleSalesPointMutation,
  useDeleteSingleSalesPointMutation,
} = salesPointApi
